import { Vehicle, VehicleLocation } from "../../../api/vehiclesApi";
import { formatRawSpeed } from "../../../utils/speedUtils";

export interface RouteStop {
  position: google.maps.LatLngLiteral;
  startedAt: string;
  endedAt: string;
  durationMs: number;
  address?: string | null;
}

interface ProcessedHistory {
  path: google.maps.LatLngLiteral[];
  pauses: RouteStop[];
  startPosition: google.maps.LatLngLiteral | null;
}

export const processVehicleHistory = (
  history: VehicleLocation[]
): ProcessedHistory => {
  const orderedHistory = [...history].reverse();
  const path: google.maps.LatLngLiteral[] = [];
  const pauses: RouteStop[] = [];

  const startPosition =
    orderedHistory.length > 0
      ? {
          lat: orderedHistory[0].latitude,
          lng: orderedHistory[0].longitude,
        }
      : null;

  for (let i = 0; i < orderedHistory.length - 1; i++) {
    const current = orderedHistory[i];
    const currentPoint = {
      lat: current.latitude,
      lng: current.longitude,
    };

    path.push(currentPoint);

    let j = i + 1;
    let lastPausePoint: VehicleLocation | null = null;

    while (j < orderedHistory.length) {
      const next = orderedHistory[j];

      if (
        next.latitude !== current.latitude ||
        next.longitude !== current.longitude
      ) {
        break;
      }

      lastPausePoint = next;
      const pauseDuration =
        new Date(next.timestamp).getTime() -
        new Date(current.timestamp).getTime();

      if (pauseDuration > 60 * 60 * 1000) {
        pauses.push({
          position: currentPoint,
          startedAt: current.timestamp,
          endedAt: lastPausePoint.timestamp,
          durationMs: pauseDuration,
          address: current.formattedAddress,
        });
        break;
      }

      j++;
    }

    i = j - 1;
  }

  if (orderedHistory.length > 0) {
    path.push({
      lat: orderedHistory[orderedHistory.length - 1].latitude,
      lng: orderedHistory[orderedHistory.length - 1].longitude,
    });
  }

  return { path, pauses, startPosition };
};

export const createStartMarker = (
  position: google.maps.LatLngLiteral,
  map: google.maps.Map
): google.maps.marker.AdvancedMarkerElement => {
  const element = document.createElement("div");
  element.style.width = "16px";
  element.style.height = "16px";
  element.style.borderRadius = "50%";
  element.style.backgroundColor = "#4CAF50";
  element.style.border = "1px solid white";
  element.style.boxShadow = "0 2px 4px rgba(0,0,0,0.3)";
  element.style.zIndex = "1000";
  element.title = "Start";

  return new google.maps.marker.AdvancedMarkerElement({
    position,
    content: element,
    map,
    zIndex: 1000,
  });
};

let activeStopInfoWindow: google.maps.InfoWindow | null = null;

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const formatStopTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) {
    return "Unknown time";
  }

  return date.toLocaleString();
};

const formatStopDuration = (durationMs: number): string => {
  const hours = Math.floor(durationMs / (1000 * 60 * 60));
  const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}m`;
};

const createStopInfoWindow = (
  stop: RouteStop,
  stopIndex: number,
  totalStops: number,
  isDarkMode: boolean = false
): google.maps.InfoWindow => {
  const colors = {
    background: isDarkMode ? "#1a1a1a" : "#ffffff",
    text: isDarkMode ? "#ffffff" : "#1a1a1a",
    secondaryText: isDarkMode ? "#9e9e9e" : "#666666",
    border: isDarkMode ? "#333333" : "#e0e0e0",
    warning: "#FF9800",
    hover: isDarkMode ? "#2a2a2a" : "#f5f5f5",
  };
  const locationText =
    stop.address ||
    `${stop.position.lat.toFixed(4)}, ${stop.position.lng.toFixed(4)}`;

  const content = `
    <div style="
      padding: 12px;
      min-width: 180px;
      max-width: 240px;
      font-family: 'Roboto', sans-serif;
      border-radius: 8px;
      background-color: ${colors.background};
      border: 1px solid ${colors.border};
    ">
      <div style="
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        margin-bottom: 10px;
      ">
        <div style="
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px 7px;
          border-radius: 4px;
          background-color: ${colors.warning};
          color: #ffffff;
          font-size: 12px;
          font-weight: 600;
        ">
          Stop ${stopIndex + 1}
        </div>
        <div style="
          font-size: 11px;
          font-weight: 500;
          color: ${colors.secondaryText};
        ">
          of ${totalStops}
        </div>
      </div>
      <div style="
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 6px 10px;
        font-size: 12px;
        color: ${colors.secondaryText};
      ">
        <div style="font-weight: 600;">Time:</div>
        <div style="color: ${colors.text}; text-align: right;">
          ${escapeHtml(formatStopTimestamp(stop.startedAt))}
        </div>
        <div style="font-weight: 600;">Duration:</div>
        <div style="text-align: right;">
          <span style="
            display: inline-block;
            padding: 2px 6px;
            border-radius: 4px;
            background-color: ${colors.hover};
            color: ${colors.warning};
            font-weight: 700;
          ">
            ${escapeHtml(formatStopDuration(stop.durationMs))}
          </span>
        </div>
        <div style="font-weight: 600;">Location:</div>
        <div style="
          color: ${colors.text};
          text-align: right;
          overflow-wrap: anywhere;
        ">
          ${escapeHtml(locationText)}
        </div>
      </div>
    </div>
  `;

  const infoWindow = new google.maps.InfoWindow({
    content,
    position: stop.position,
    pixelOffset: new google.maps.Size(0, -10),
  });

  google.maps.event.addListenerOnce(infoWindow, "domready", () => {
    const bubbleContainer = document.querySelector(".gm-style-iw-c");
    if (bubbleContainer) {
      // @ts-ignore
      bubbleContainer.style.padding = "0";
      // @ts-ignore
      bubbleContainer.style.backgroundColor = "transparent";
    }

    const contentContainer = document.querySelector(".gm-style-iw-d");
    if (contentContainer) {
      // @ts-ignore
      contentContainer.style.overflow = "visible";
      // @ts-ignore
      contentContainer.style.backgroundColor = "transparent";
    }

    const closeButton = document.querySelector("button.gm-ui-hover-effect");
    if (closeButton) {
      // @ts-ignore
      closeButton.style.filter = isDarkMode ? "invert(1)" : "none";
    }
  });

  google.maps.event.addListener(infoWindow, "closeclick", () => {
    if (activeStopInfoWindow === infoWindow) {
      activeStopInfoWindow = null;
    }
  });

  return infoWindow;
};

export const createPauseMarker = (
  stop: RouteStop,
  map: google.maps.Map,
  stopIndex: number,
  totalStops: number,
  isDarkMode: boolean = false
): google.maps.marker.AdvancedMarkerElement => {
  const element = document.createElement("div");
  element.style.width = "16px";
  element.style.height = "16px";
  element.style.borderRadius = "50%";
  element.style.backgroundColor = "#FF9800";
  element.style.border = "1px solid white";
  element.style.boxShadow = "0 2px 4px rgba(0,0,0,0.3)";
  element.style.zIndex = "100";
  element.style.cursor = "pointer";
  element.title = `Stop ${stopIndex + 1} of ${totalStops}`;

  const marker = new google.maps.marker.AdvancedMarkerElement({
    position: stop.position,
    content: element,
    map,
    zIndex: 100,
  });

  element.addEventListener("mouseenter", () => {
    if (activeStopInfoWindow) {
      activeStopInfoWindow.close();
    }

    activeStopInfoWindow = createStopInfoWindow(
      stop,
      stopIndex,
      totalStops,
      isDarkMode
    );
    activeStopInfoWindow.open({ map, anchor: marker });
  });

  element.addEventListener("mouseleave", () => {
    if (activeStopInfoWindow) {
      activeStopInfoWindow.close();
      activeStopInfoWindow = null;
    }
  });

  return marker;
};

export const createHistoryPolyline = (
  path: google.maps.LatLngLiteral[],
  map: google.maps.Map,
  isDarkMode: boolean = false
): google.maps.Polyline => {
  return new google.maps.Polyline({
    path,
    map,
    strokeColor: isDarkMode ? "#90caf9" : "#3f51b5",
    strokeOpacity: 0.8,
    strokeWeight: 5,
  });
};

export const createVehicleInfoWindow = (
  vehicle: Vehicle,
  position: google.maps.LatLngLiteral,
  map: google.maps.Map,
  isDarkMode: boolean = false,
  onCenterVehicle?: () => void,
  onPanOutRoute?: () => void
): google.maps.InfoWindow => {
  const colors = {
    background: isDarkMode ? "#1a1a1a" : "#ffffff",
    text: isDarkMode ? "#ffffff" : "#1a1a1a",
    secondaryText: isDarkMode ? "#9e9e9e" : "#666666",
    border: isDarkMode ? "#333333" : "#e0e0e0",
    active: "#4CAF50",
    inactive: "#f44336",
  };

  const content = `
    <div style="
      padding: 16px 24px 16px 16px;
      min-width: 200px;
      font-family: 'Roboto', sans-serif;
      border-radius: 8px;
      background-color: ${colors.background};
      border: 1px solid ${colors.border};
    ">
      <div style="
        display: flex;
        align-items: center;
        margin-bottom: 8px;
      ">
        <div style="
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: ${
            vehicle.lastStatus === "Active" ? colors.active : colors.inactive
          };
          margin-right: 8px;
        "></div>
        <div style="
          font-size: 14px;
          font-weight: 500;
          color: ${colors.text};
        ">${vehicle.name}</div>
      </div>
      <div style="
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 4px 8px;
        font-size: 12px;
        color: ${colors.secondaryText};
        margin-bottom: 12px;
      ">
        <div>Type:</div>
        <div style="color: ${colors.text};">${vehicle.type || "Unknown"}</div>
        <div>Speed:</div>
        <div style="color: ${colors.text};">${formatRawSpeed(
    vehicle.lastSpeed
  )}</div>
        <div>Registration:</div>
        <div style="color: ${colors.text};">${
    vehicle.registrationNumber || "N/A"
  }</div>
        <div>Make/Model:</div>
        <div style="color: ${colors.text};">${vehicle.make || ""} ${
    vehicle.model || "Unknown"
  }</div>
      </div>
      ${
        onCenterVehicle || onPanOutRoute
          ? `
      <div style="
        display: flex;
        gap: 8px;
        padding-top: 8px;
        border-top: 1px solid ${colors.border};
      ">
        ${
          onCenterVehicle
            ? `
        <button id="center-vehicle-btn" style="
          flex: 1;
          padding: 6px 12px;
          font-size: 11px;
          font-weight: 500;
          color: ${colors.text};
          background-color: ${colors.background};
          border: 1px solid ${colors.border};
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
        " onmouseover="this.style.backgroundColor='${
          isDarkMode ? "#333333" : "#f5f5f5"
        }'" onmouseout="this.style.backgroundColor='${colors.background}'">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
          Center
        </button>
        `
            : ""
        }
        ${
          onPanOutRoute
            ? `
        <button id="pan-out-route-btn" style="
          flex: 1;
          padding: 6px 12px;
          font-size: 11px;
          font-weight: 500;
          color: ${colors.text};
          background-color: ${colors.background};
          border: 1px solid ${colors.border};
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
        " onmouseover="this.style.backgroundColor='${
          isDarkMode ? "#333333" : "#f5f5f5"
        }'" onmouseout="this.style.backgroundColor='${colors.background}'">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
            <path d="M12 10h-2v3H9v-3H7V9h2V6h1v3h2v1z"/>
          </svg>
          Pan Out
        </button>
        `
            : ""
        }
      </div>
      `
          : ""
      }
    </div>
  `;

  const infoWindow = new google.maps.InfoWindow({
    content,
    position,
    pixelOffset: new google.maps.Size(0, -10),
    disableAutoPan: true,
  });

  google.maps.event.addListenerOnce(infoWindow, "domready", () => {
    const bubbleContainer = document.querySelector(".gm-style-iw-c");
    if (bubbleContainer) {
      // @ts-ignore
      bubbleContainer.style.padding = "0";
      // @ts-ignore
      bubbleContainer.style.width = "fit-content";
      // @ts-ignore
      bubbleContainer.style.backgroundColor = "transparent";
      // @ts-ignore
      bubbleContainer.style.boxShadow = isDarkMode
        ? "0 2px 7px 1px rgba(0, 0, 0, 0.3)"
        : "0 2px 7px 1px rgba(0, 0, 0, 0.1)";
    }

    const contentContainer = document.querySelector(".gm-style-iw-d");
    if (contentContainer) {
      // @ts-ignore
      contentContainer.style.overflow = "visible";
      // @ts-ignore
      contentContainer.style.backgroundColor = "transparent";
    }

    const closeButton = document.querySelector("button.gm-ui-hover-effect");
    if (closeButton) {
      // @ts-ignore
      closeButton.style.top = "4px";
      // @ts-ignore
      closeButton.style.right = "4px";
      // @ts-ignore
      closeButton.style.position = "absolute";
      // @ts-ignore
      closeButton.style.filter = isDarkMode ? "invert(1)" : "none";
    }

    // Add event listeners for action buttons
    const centerVehicleBtn = document.getElementById("center-vehicle-btn");
    if (centerVehicleBtn && onCenterVehicle) {
      centerVehicleBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        onCenterVehicle();
      });
    }

    const panOutRouteBtn = document.getElementById("pan-out-route-btn");
    if (panOutRouteBtn && onPanOutRoute) {
      panOutRouteBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        onPanOutRoute();
      });
    }
  });

  return infoWindow;
};
